import { Alert, Input, Modal, Select, Space } from 'antd';
import React, { useState } from 'react';

import type { ResolveSharePointSharingLinkResponseDTO } from '@/api/projects/api';
import { SharePointLinkResolver } from '@/components/common/SharePointLinkResolver';
import { createLogger } from '@/utils/logging';

const logger = createLogger('AddProjectLinkModal', 'component');

export type LinkSourceType = 'sharepoint' | 'other';

export type AddProjectLinkPayload =
  | { type: 'sharepoint'; resolved: ResolveSharePointSharingLinkResponseDTO }
  | { type: 'other'; label: string; url: string };

const SOURCE_OPTIONS: Array<{ label: string; value: LinkSourceType }> = [
  { label: 'SharePoint', value: 'sharepoint' },
  { label: 'Other', value: 'other' },
];

export interface AddProjectLinkModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AddProjectLinkPayload) => Promise<void>;
  clientId: string;
  projectId: string;
  accessToken?: string | null;
  microsoftOboAssertion?: string | null;
}

/** "Add Project Link" modal: choose SharePoint (saves to project details) or Other (free-form URL into link table). */
export function AddProjectLinkModal({
  open,
  onClose,
  onSubmit,
  clientId,
  projectId,
  accessToken,
  microsoftOboAssertion,
}: Readonly<AddProjectLinkModalProps>): React.ReactElement {
  const [sourceType, setSourceType] = useState<LinkSourceType>('other');
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [resolved, setResolved] = useState<ResolveSharePointSharingLinkResponseDTO | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetState = (): void => {
    setSourceType('other');
    setLabel('');
    setUrl('');
    setResolved(null);
    setError(null);
  };

  const handleClose = (): void => {
    resetState();
    onClose();
  };

  const handleSourceTypeChange = (value: LinkSourceType): void => {
    setSourceType(value);
    setError(null);
  };

  const handleSubmit = async (): Promise<void> => {
    let payload: AddProjectLinkPayload;

    if (sourceType === 'sharepoint') {
      if (!resolved) {
        setError('Resolve a SharePoint sharing link first');
        return;
      }
      payload = { type: 'sharepoint', resolved };
    } else {
      const trimmedLabel = label.trim();
      if (!trimmedLabel) {
        setError('Please enter a link label');
        return;
      }
      const trimmedUrl = url.trim();
      let parsedUrl: URL;
      try {
        parsedUrl = new URL(trimmedUrl);
      } catch {
        setError('Please enter a valid URL');
        return;
      }
      if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
        setError('URL must start with http:// or https://');
        return;
      }
      payload = { type: 'other', label: trimmedLabel, url: trimmedUrl };
    }

    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(payload);
      handleClose();
    } catch (submitError) {
      logger.error('Failed to add project link:', submitError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Add Project Link"
      open={open}
      onCancel={handleClose}
      onOk={() => void handleSubmit()}
      okText="Add"
      confirmLoading={submitting}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <label htmlFor="add-link-source-type">Link Type</label>
          <Select<LinkSourceType>
            id="add-link-source-type"
            value={sourceType}
            onChange={handleSourceTypeChange}
            options={SOURCE_OPTIONS}
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>

        {sourceType === 'sharepoint' ? (
          <SharePointLinkResolver
            accessToken={accessToken}
            microsoftOboAssertion={microsoftOboAssertion}
            clientId={clientId}
            projectId={projectId}
            resolved={resolved}
            onResolved={setResolved}
          />
        ) : (
          <>
            <div>
              <label htmlFor="add-link-label">Link Label</label>
              <Input
                id="add-link-label"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="e.g., Project Jira, Documentation"
                style={{ marginTop: 4 }}
              />
            </div>
            <div>
              <label htmlFor="add-link-url">URL</label>
              <Input
                id="add-link-url"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="e.g., https://example.com"
                style={{ marginTop: 4 }}
              />
            </div>
          </>
        )}

        {error ? <Alert type="error" message={error} showIcon /> : null}
      </Space>
    </Modal>
  );
}
